import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Dialog, DialogPanel } from '@tremor/react';

interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type Resolver = (ok: boolean) => void;

interface OpenState extends ConfirmOptions { resolve: Resolver }

const ConfirmContext = createContext<((opts: ConfirmOptions) => Promise<boolean>) | null>(null);

/**
 * Wraps the app and exposes the imperative confirm() function via context.
 * Renders one shared dialog so we don't multiply portals.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<OpenState | null>(null);

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setOpen({ ...opts, resolve });
    });
  }, []);

  const close = (ok: boolean) => {
    if (open) open.resolve(ok);
    setOpen(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={!!open} onClose={() => close(false)} static>
        <DialogPanel className="bg-gray-900 border border-gray-800 max-w-md">
          <h3 className="text-base font-semibold text-gray-100">{open?.title}</h3>
          {open?.message && (
            <div className="mt-2 text-sm text-gray-400">
              {open.message}
            </div>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => close(false)}
              className="px-3 py-1.5 text-sm rounded bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700"
              autoFocus
            >
              {open?.cancelLabel || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => close(true)}
              className={
                open?.danger
                  ? 'px-3 py-1.5 text-sm rounded bg-red-900/60 hover:bg-red-900 text-red-100 border border-red-900'
                  : 'px-3 py-1.5 text-sm rounded bg-blue-900/60 hover:bg-blue-900 text-blue-100 border border-blue-800'
              }
            >
              {open?.confirmLabel || 'Confirm'}
            </button>
          </div>
        </DialogPanel>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/**
 * useConfirm — returns an async confirm() function. Resolves to true if the
 * user clicked the confirm button, false otherwise (including dismissing).
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
